import { orchestrator, mutatorAction } from 'satcheljs';
import type { ProjectId, AssetId } from 'owa-timestream-types';
import { updateAssetParent, reorderAssetChildren, setAnnouncement } from '../actions';
import getAssetOrUndefined from '../selectors/getAssetOrUndefined';
import getProject from '../selectors/getProject';
import getAsset from '../selectors/getAsset';
import patchAsset from '../utils/patchAsset';
import resolveAssetId from '../assetId/resolveAssetId';
import {
    widgetAddedToCollectionAnnouncement,
    widgetRemovedFromCollectionAnnouncement,
} from './updateAssetParent.locstring.json';
import { focusAsset } from 'owa-timestream-asset-sdk-actions';
import loc from 'owa-localize';

orchestrator(
    updateAssetParent,
    async ({
        projectId,
        assetId,
        parentId,
        order,
        reorderChildren,
        x,
        y,
        parentUpdateId,
        partialOriginalAsset,
    }) => {
        updateAssetParentClientSide(projectId, assetId, parentId, order, parentUpdateId);

        const [outlookSpaceAssetId, parentAssetId] = await Promise.all([
            resolveAssetId(assetId),
            parentId && resolveAssetId(parentId),
        ]);
        if (outlookSpaceAssetId && (!parentId || parentAssetId)) {
            let newPosition = {};
            if (x != null && y != null) {
                newPosition = { x: x, y: y };
            }

            await patchAsset(
                projectId,
                assetId,
                { parentId: parentAssetId ?? null, order, ...newPosition },
                partialOriginalAsset,
                asset => asset.parentUpdateId === parentUpdateId
            );

            if (reorderChildren && parentAssetId) {
                reorderAssetChildren(projectId, parentAssetId);
            }
            const { type } = getAsset(projectId, assetId);
            if (!parentAssetId) {
                setAnnouncement(loc(widgetRemovedFromCollectionAnnouncement, type));
            } else {
                setAnnouncement(loc(widgetAddedToCollectionAnnouncement, type));
            }
            delayedFocusAsset(projectId, assetId);
        }
    }
);

const updateAssetParentClientSide = mutatorAction(
    'updateAssetParentClientSide',
    (
        projectId: ProjectId,
        clientAssetId: AssetId,
        parentId: AssetId | null,
        order: number | undefined,
        parentUpdateId: number
    ) => {
        let { renderingOrder } = getProject(projectId);
        const clientAsset = getAssetOrUndefined(projectId, clientAssetId);
        if (clientAsset) {
            // update the local asset
            clientAsset.parentId = parentId;
            if (order != undefined) {
                clientAsset.order = order;
            }
            clientAsset.parentUpdateId = parentUpdateId;

            if (parentId) {
                // adding the asset to a bucket
                const parentAsset = getAssetOrUndefined(projectId, parentId);

                if (parentAsset) {
                    // remove asset from the rendering order
                    const index = renderingOrder.indexOf(clientAssetId);
                    if (index >= 0) {
                        renderingOrder.splice(index, 1);
                    }

                    // update bucket to include the asset
                    parentAsset.children = parentAsset.children ?? [];
                    parentAsset.children?.push(clientAssetId);
                }
            } else {
                // parentId undefined, so removing from bucket and adding back to rendering order
                if (!renderingOrder.includes(clientAssetId)) {
                    renderingOrder.push(clientAssetId);
                }
            }
        } else {
            // The local asset was deleted from the client store before the server create call completed.
            // The server ID resolution would cause the delete operation to fire, cleaning it up on the server.
        }
    }
);

function delayedFocusAsset(projectId, selectedAsset) {
    // need to delay call to give asset time to render and have a focus function
    setTimeout(() => {
        focusAsset(projectId, selectedAsset);
    }, 10);
}
